import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  ArrowLeft, 
  Save, 
  Sparkles, 
  Loader2, 
  Image as ImageIcon,
  Bold,
  Underline,
  Highlighter,
  FolderKanban
} from 'lucide-react';
import { toast } from 'sonner';

const industries = [
  { value: 'insurtech', label: 'Insurtech' },
  { value: 'fintech', label: 'Fintech' },
  { value: 'oil_gas', label: 'Oil & Gas' },
  { value: 'telecom', label: 'Telecom' },
  { value: 'airline', label: 'Airline' },
  { value: 'others', label: 'Others' },
];

export default function AddProject() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const isEditing = !!projectId;

  const [loading, setLoading] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [formData, setFormData] = useState({
    headline: '',
    industry: '',
    technology: '',
    details: '',
    featured_image: '',
    status: 'completed',
  });

  useEffect(() => {
    if (isEditing) {
      fetchProject();
    }
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}`);
      const project = response.data;
      setFormData({
        headline: project.headline || '',
        industry: project.industry || '',
        technology: project.technology || '',
        details: project.details || '',
        featured_image: project.featured_image || '',
        status: project.status || 'completed',
      });
    } catch (error) {
      console.error('Error fetching project:', error);
      toast.error('Failed to fetch project');
      navigate('/admin/projects');
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    console.log('Form data:', formData);
    
    if (!formData.headline || !formData.industry || !formData.technology || !formData.details) {
      toast.error('Please fill in all required fields');
      console.log('Validation failed:', {
        headline: !!formData.headline,
        industry: !!formData.industry,
        technology: !!formData.technology,
        details: !!formData.details
      });
      return;
    }

    setLoading(true);
    try {
      if (isEditing) {
        await axios.put(`${API}/projects/${projectId}`, formData);
        toast.success('Project updated successfully');
      } else {
        await axios.post(`${API}/projects`, formData);
        toast.success('Project created successfully');
      }
      navigate('/admin/projects');
    } catch (error) {
      console.error('Error saving project:', error);
      toast.error('Failed to save project');
    } finally {
      setLoading(false);
    }
  };

  const generateAIImage = async () => {
    if (!formData.headline || !formData.industry) {
      toast.error('Please enter headline and select industry first');
      return;
    }

    setGeneratingImage(true);
    try {
      const response = await axios.post(`${API}/generate-project-image`, {
        headline: formData.headline,
        industry: formData.industry,
        technology: formData.technology || 'modern technology',
        details: formData.details,
      });

      if (response.data.image_base64) {
        const imageUrl = `data:image/png;base64,${response.data.image_base64}`;
        setFormData(prev => ({ ...prev, featured_image: imageUrl }));
        toast.success('AI image generated successfully!');
      }
    } catch (error) {
      console.error('Error generating image:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate AI image');
    } finally {
      setGeneratingImage(false);
    }
  };

  // Rich text editor functions
  const applyFormat = (format) => {
    const textarea = document.getElementById('project-details');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = formData.details.substring(start, end);
    
    if (!selectedText) {
      toast.error('Please select some text first');
      return;
    }

    let formattedText = '';
    switch (format) {
      case 'bold':
        formattedText = `<strong>${selectedText}</strong>`;
        break;
      case 'underline':
        formattedText = `<u>${selectedText}</u>`;
        break;
      case 'highlight':
        formattedText = `<mark>${selectedText}</mark>`;
        break;
      default:
        formattedText = selectedText;
    }

    const newDetails = 
      formData.details.substring(0, start) + 
      formattedText + 
      formData.details.substring(end);
    
    setFormData(prev => ({ ...prev, details: newDetails }));
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/admin/projects')}
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                  {isEditing ? 'Edit Project' : 'Add New Project'}
                </h1>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {isEditing ? 'Update project details' : 'Create a new project for your portfolio'}
                </p>
              </div>
            </div>
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {isEditing ? 'Update Project' : 'Save Project'}
            </Button>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Project Headline */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FolderKanban className="w-5 h-5 text-blue-600" />
                Project Headline
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Input
                placeholder="Enter a compelling headline for the project"
                value={formData.headline}
                onChange={(e) => setFormData({ ...formData, headline: e.target.value })}
                className="text-lg"
                data-testid="project-headline"
              />
            </CardContent>
          </Card>

          {/* Industry & Technology */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Industry</CardTitle>
              </CardHeader>
              <CardContent>
                <Select
                  value={formData.industry}
                  onValueChange={(value) => setFormData({ ...formData, industry: value })}
                >
                  <SelectTrigger data-testid="industry-select">
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    {industries.map((ind) => (
                      <SelectItem key={ind.value} value={ind.value}>
                        {ind.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Technology Used</CardTitle>
              </CardHeader>
              <CardContent>
                <Input
                  placeholder="e.g., React, Python, AWS, MongoDB"
                  value={formData.technology}
                  onChange={(e) => setFormData({ ...formData, technology: e.target.value })}
                  data-testid="technology-input"
                />
              </CardContent>
            </Card>
          </div>

          {/* Project Details with Rich Text */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Project Details</CardTitle>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => applyFormat('bold')}
                    title="Bold"
                  >
                    <Bold className="w-4 h-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => applyFormat('underline')}
                    title="Underline"
                  >
                    <Underline className="w-4 h-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => applyFormat('highlight')}
                    title="Highlight"
                  >
                    <Highlighter className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <textarea
                id="project-details"
                placeholder="Describe the project in detail. Select text and use the formatting buttons above to add bold, underline, or highlight..."
                value={formData.details}
                onChange={(e) => setFormData({ ...formData, details: e.target.value })}
                className="w-full min-h-[250px] p-4 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white resize-y focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="project-details"
              />
              <p className="text-xs text-slate-500 mt-2">
                Tip: Select text and click Bold, Underline, or Highlight to format. HTML tags will be rendered on the website.
              </p>
            </CardContent>
          </Card>

          {/* AI Image Generation */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-purple-600" />
                Featured Image
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4">
                <Input
                  placeholder="Image URL or generate with AI"
                  value={formData.featured_image}
                  onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })}
                  className="flex-1"
                  data-testid="featured-image-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={generateAIImage}
                  disabled={generatingImage || !formData.headline || !formData.industry}
                  className="whitespace-nowrap"
                >
                  {generatingImage ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate AI Image
                    </>
                  )}
                </Button>
              </div>

              {/* Image Preview */}
              {formData.featured_image && (
                <div className="relative rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800">
                  <img
                    src={formData.featured_image}
                    alt="Project preview"
                    className="w-full h-64 object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="absolute top-3 right-3"
                    onClick={() => setFormData({ ...formData, featured_image: '' })}
                  >
                    Remove
                  </Button>
                </div>
              )}

              {!formData.featured_image && (
                <div className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl p-8 text-center">
                  <ImageIcon className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-500 dark:text-slate-400">
                    Enter headline and select industry, then click "Generate AI Image"
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Status */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Project Status</CardTitle>
            </CardHeader>
            <CardContent>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="planning">Planning</SelectItem>
                  <SelectItem value="on_hold">On Hold</SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Submit Button (Mobile) */}
          <div className="md:hidden">
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {isEditing ? 'Update Project' : 'Save Project'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
